import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useId } from "react";
import type { MoveToSetTarget } from "../../../../api/pipelineBatchUpdates.service.js";
import { describeMoveToSetProgress } from "./describeMoveToSetProgress.js";
import { MoveToSetFamilyConflicts } from "./MoveToSetFamilyConflicts.js";
import { MoveToSetParsedSummary } from "./MoveToSetParsedSummary.js";
import { MoveToSetRunReport } from "./MoveToSetRunReport.js";
import { useMoveToSetRun, type ApplyBlock } from "./MoveToSetRunProvider.js";
import { countByType, failedTargets } from "./useMoveToSetBatch.js";
import styles from "../PipelineBrowser.module.css";

const BATCH_SIZE_OPTIONS = [100, 250, 500] as const;

function typeBreakdown(targets: MoveToSetTarget[]): string {
  const counts = countByType(targets);
  return `set_testimonial: ${counts.set_testimonial}\nclerk_testimonial: ${counts.clerk_testimonial}`;
}

function applyHint(block: ApplyBlock): string {
  if (block.kind === "no_dry_run") return "Apply needs a finished dry run of this file first.";
  if (block.kind === "settings_changed") {
    return "Settings changed since the dry run. Run the dry run again.";
  }
  return `${block.blocking} blocking family ${block.blocking === 1 ? "conflict" : "conflicts"}. Fix the file and run the dry run again.`;
}

export function MoveToSetCsvSection() {
  const hintId = useId();
  const {
    batchSize,
    setBatchSize,
    file,
    parsing,
    parseError,
    selectFile,
    includeNoCompanyId,
    setIncludeNoCompanyId,
    selection,
    runSkippedCount,
    conflicts,
    applyBlock,
    startDryRun,
    startApply,
    retryFailedRows,
    running,
    runKind,
    stopped,
    dryRun,
    progress,
    results,
    totals,
    error,
    auditEmitFailed,
    blockedBy,
    stop,
    registerSection,
  } = useMoveToSetRun();

  useEffect(() => registerSection(), [registerSection]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0];
      event.target.value = "";
      if (selected) void selectFile(selected);
    },
    [selectFile],
  );

  const rows = selection?.rows ?? [];
  const skippedCount = selection?.skipped.length ?? 0;
  const invalidCount = file?.parsed.invalid.length ?? 0;
  const canRun =
    !running && !parsing && file !== null && rows.length > 0 && file.parsed.duplicates.length === 0;
  const familyWarnings = conflicts?.filter(conflict => conflict.severity === "warning").length ?? 0;

  const handleApply = useCallback(() => {
    const leftOut = [
      skippedCount > 0 ? `${skippedCount} skipped` : null,
      invalidCount > 0 ? `${invalidCount} invalid` : null,
    ].filter(Boolean);
    const mates =
      familyWarnings > 0
        ? `\n\nThe dry run listed ${familyWarnings} family ${familyWarnings === 1 ? "warning" : "warnings"} under Family check. Read them before you continue.`
        : "";
    const confirmed = window.confirm(
      `Move ${rows.length} testimonials to the set?\n\n${typeBreakdown(rows)}${leftOut.length > 0 ? `\n\nNot sent: ${leftOut.join(", ")} rows.` : ""}${mates}\n\nThis clears the barrister link and sets the testimonial type in approval-api and the pipeline, for these rows and every testimonial in their processing item family. There is no undo button.\n\nContinue?`,
    );
    if (!confirmed) return;
    void startApply();
  }, [startApply, rows, skippedCount, invalidCount, familyWarnings]);

  const handleRetryFailed = useCallback(() => {
    const targets = failedTargets(results);
    const confirmed = window.confirm(
      `Retry ${targets.length} failed rows?\n\n${typeBreakdown(targets)}\n\nThis is not a dry run. There is no undo button.\n\nContinue?`,
    );
    if (!confirmed) return;
    void retryFailedRows();
  }, [retryFailedRows, results]);

  const hasResult = !running && progress.totalRows > 0;
  const canRetry = !running && !dryRun && totals.failed > 0 && applyBlock === null;
  const showApplyHint = canRun && applyBlock !== null;
  const statusLabel =
    runKind === "retry" ? "Retrying failed rows" : dryRun ? "Dry run" : "Applying";

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Move testimonials to the set from CSV</div>
      <div className={styles.infoBanner}>
        Two CSV formats are accepted. The simple one has the columns{" "}
        <code>testimonial_id,testimonial_type</code>, with a type of <code>set_testimonial</code> or{" "}
        <code>clerk_testimonial</code>. The other is the move-to-set worklist export, as it is: rows
        whose <code>move_change</code> starts with <code>none:</code> are skipped, and the target
        type comes from <code>move_change</code>, or from <code>filed_as</code> when{" "}
        <code>move_change</code> is <code>clear contact_id</code>. Moving clears the barrister link
        and sets the testimonial type, so the quote shows on the set page and not on the
        barrister&apos;s. Every testimonial in the same processing item family (translation copies,
        Round 2) is moved with it. The dry run checks those family-mates against the file, and Apply
        stays off until a dry run of this file and these settings finishes with no blocking
        conflicts. Before each Apply batch is written, its ids are checked again as a dry run, and
        the run stops without writing that batch if a blocking conflict has appeared since.
        approval-api is updated first, then the pipeline. Failed rows were not moved in the
        pipeline, though approval-api may already show the change. Re-running them is safe.
      </div>

      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>Batch size:</span>
        <select
          className={styles.filterSelect}
          aria-label="Move to set batch size"
          value={batchSize}
          disabled={running}
          onChange={e => setBatchSize(Number(e.target.value))}
        >
          {BATCH_SIZE_OPTIONS.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className={styles.syncStateLabel}>CSV file:</span>
        <input
          type="file"
          accept=".csv"
          aria-label="Move to set CSV file"
          disabled={running || parsing}
          onChange={handleFileChange}
        />
      </div>

      {parsing && <div className={styles.syncStateMessage}>Parsing CSV…</div>}
      {parseError && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {parseError}
        </div>
      )}
      {file && selection && (
        <MoveToSetParsedSummary
          fileName={file.name}
          parsed={file.parsed}
          selection={selection}
          includeNoCompanyId={includeNoCompanyId}
          onIncludeNoCompanyIdChange={setIncludeNoCompanyId}
          disabled={running}
        />
      )}

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!canRun}
          onClick={() => void startDryRun()}
        >
          Dry run
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={!canRun || applyBlock !== null}
          aria-describedby={showApplyHint ? hintId : undefined}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={!running}
          onClick={stop}
        >
          Stop
        </Button>
        {showApplyHint && applyBlock && (
          <span id={hintId} className={styles.mappingHint}>
            {applyHint(applyBlock)}
          </span>
        )}
      </div>

      {conflicts && <MoveToSetFamilyConflicts conflicts={conflicts} />}

      {running && (
        <>
          <div className={styles.warningBox}>
            Do not close or refresh this tab. The move runs from this browser tab in batches, and
            closing or refreshing stops it part way. Other staff-ui pages are fine: the run carries
            on, a banner at the top shows its progress, and the report is here when you come back.
            Re-uploading the same file is safe, rows already moved come back as unchanged.
          </div>
          <div className={styles.syncStateMessage} role="status">
            {statusLabel}: {describeMoveToSetProgress(progress)}
          </div>
          <div className={styles.syncStateMessage}>
            {dryRun ? "Would move" : "Moved"} {totals.moved} · unchanged {totals.unchanged} · not
            found {totals.notFound} · failed {totals.failed} · family-mates{" "}
            {dryRun ? "would move" : "moved"} {totals.familyMatesMoved}
          </div>
        </>
      )}

      {auditEmitFailed && (
        <div className={styles.warningBox} role="alert">
          Audit events could not be emitted for at least one batch. The moves were saved, but they
          are missing from the audit log.
        </div>
      )}

      {error && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {error}
        </div>
      )}

      {blockedBy.length > 0 && (
        <MoveToSetFamilyConflicts
          conflicts={blockedBy}
          heading="Pre-check"
          listLabel="Conflicts that stopped the run"
        />
      )}

      {hasResult && (
        <MoveToSetRunReport
          totals={totals}
          skippedCount={runSkippedCount}
          results={results}
          progress={progress}
          dryRun={dryRun}
          stopped={stopped}
          blocked={blockedBy.length > 0}
          runKind={runKind}
          canRetry={canRetry}
          retryDescribedBy={showApplyHint ? hintId : undefined}
          onRetryFailed={handleRetryFailed}
        />
      )}
    </div>
  );
}
